// Main layout component - Only renders content area, Header/Sidebar are in LayoutShell
import { useEffect, useRef, memo } from 'react';
import { Outlet } from 'react-router-dom';
import { ScrollRestoration } from '@components/routing/ScrollRestoration';
import { useFileStore } from '@stores/useFileStore';
import { useAuthStore } from '@stores/authStore';

const MainLayoutComponent: React.FC = () => {
  // Use selective selectors to prevent re-renders on route changes
  const fetchFiles = useFileStore((state) => state.fetchFiles);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const filesFetchedRef = useRef(false);

  // Fetch files only once when layout mounts and user is authenticated
  // Don't refetch on route changes - this prevents unnecessary reloads
  useEffect(() => {
    if (isAuthenticated && !filesFetchedRef.current) {
      console.log('📂 MainLayout: Fetching files (first time)...');
      fetchFiles();
      filesFetchedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Only depend on isAuthenticated, not fetchFiles

  return (
    <>
      <ScrollRestoration />
      {/* Responsive main content area - with padding for miniplayer */}
      {/* CRITICAL: Content must be visible at all breakpoints, starting from 200px */}
      <main
        className="relative ml-0 pt-[66px] sm:pt-[71px] md:pt-[76px] lg:pt-[81px] xl:pt-[86px] px-2 sm:px-4 md:px-6 lg:px-12 xl:px-16 2xl:px-20 pb-20 md:pb-24 min-w-0 w-full overflow-x-hidden bg-gradient-to-br from-[#050510] via-[#0a0a15] via-[#0f0c29] via-[#141428] to-[#16213e] min-h-[calc(100vh-66px)] sm:min-h-[calc(100vh-71px)] md:min-h-[calc(100vh-76px)] lg:min-h-[calc(100vh-81px)] xl:min-h-[calc(100vh-86px)] sidebar-offset-main"
        style={{ minWidth: '200px' }}
      >
        <div className="w-full min-w-0 relative">
          <Outlet />
        </div>
      </main>
    </>
  );
};

// Memoize MainLayout to prevent re-renders on route changes
export const MainLayout = memo(MainLayoutComponent);
MainLayout.displayName = 'MainLayout';

